// Récapitulatif des quantités du dossier (câblage par type, nomenclature du schéma) et envoi vers un devis brouillon.
// Le bouton d'envoi reste désactivé, avec sa raison, tant qu'une échelle ou un cheminement n'est pas validé.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FilePlus2 } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { Alerte, Badge, Bouton, ChampChoix, Chargement, EtatErreur, LienBouton, Panneau, useNotifier } from '../../components/ui.tsx';
import { nombre } from '../../lib/format.ts';
import type { Quantites } from './modele.ts';
import { ChargeFrigorifique } from './ChargeFrigorifique.tsx';

interface DevisResume { id: string; number: string; title: string; revisions: { id: string; rev_no: number; variant: string; status: string }[] }

export function RecapQuantites({ dossierId }: { dossierId: string }) {
  const { peut } = useSession();
  const notifier = useNotifier();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['conception-quantites', dossierId], queryFn: () => api.get<Quantites>(`/api/dossiers/${dossierId}/quantites`) });
  const devis = useQuery({
    queryKey: ['conception-devis', dossierId], enabled: peut('devis.lire') && !!q.data?.envoiDevisDisponible,
    queryFn: () => api.get<DevisResume[]>(`/api/dossiers/${dossierId}/devis`), retry: false,
  });
  const brouillons = (devis.data ?? []).flatMap((d) => d.revisions.filter((r) => r.status === 'brouillon')
    .map((r) => ({ valeur: r.id, libelle: `${d.number} — ${d.title} (révision ${r.rev_no}${r.variant !== 'Base' ? `, ${r.variant}` : ''})` })));
  const [revision, setRevision] = useState('');
  useEffect(() => { if (!brouillons.some((b) => b.valeur === revision)) setRevision(brouillons[0]?.valeur ?? ''); }, [brouillons.map((b) => b.valeur).join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  const envoi = useMutation({
    mutationFn: () => api.post<{ lignes: number; resultat: { creees: string[]; ignorees: number } | null }>(`/api/dossiers/${dossierId}/quantites/vers-devis`, { revisionId: revision }),
    onSuccess: (r) => {
      const n = r.resultat?.creees.length ?? r.lignes; const deja = r.resultat?.ignorees ?? 0;
      notifier(`${n} ligne(s) ajoutée(s) au devis${deja ? `. ${deja} ligne(s) déjà présente(s). Consultez les métrés modifiés dans le devis pour les mettre à jour` : ''}.`);
      void qc.invalidateQueries({ queryKey: ['conception-devis', dossierId] });
    },
    onError: (e) => notifier(messageErreur(e), true),
  });

  if (q.isLoading) return <Chargement lignes={3} libelle="Calcul des quantités…" />;
  if (q.isError || !q.data) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const x = q.data;

  const raison = !x.envoiDevisDisponible ? 'Le module Devis n’est pas disponible : l’envoi des quantités est impossible pour l’instant.'
    : !peut('devis.ecrire') ? 'Vos droits ne permettent pas de modifier un devis.'
    : x.plansNonValides.length ? `Métrés non fiables : échelle non validée sur ${x.plansNonValides.map((p) => `« ${p.name} »`).join(', ')}.`
    : x.cheminementsNonValides.length ? `${x.cheminementsNonValides.length} cheminement(s) sans validation technique.`
    : !x.toutFiable ? 'Métré incomplet : hauteur ou diamètre à saisir.'
    : !x.cablage.length && !x.tuyauteries.length && !x.nomenclature.some((n) => !n.existant) ? 'Aucune quantité à envoyer.'
    : devis.isLoading ? 'Chargement des devis…'
    : !brouillons.length ? 'Aucun devis brouillon dans ce dossier.'
    : null;

  return (
    <div className="pile-l">
      <Panneau titre="Câblage par type de câble" serre>
        {!x.cablage.length ? <p className="petit discret" style={{ padding: 'var(--e-4) var(--e-5)' }}>Aucun cheminement tracé dans ce dossier.</p> : (
          <div className="tableau-conteneur">
            <table className="tableau cch-tableau">
              <thead><tr><th>Type de câble</th><th className="num">Cheminements</th><th className="num">Total posé</th><th className="num">Réserve</th>
                <th className="num">Chutes</th><th className="num">À commander</th><th>Fiabilité</th></tr></thead>
              <tbody>
                {x.cablage.map((g) => (
                  <tr key={g.cle}>
                    <td data-libelle="Type de câble">{g.typeCable ?? <span className="inconnu">Type à préciser</span>}<div className="tres-petit discret">{g.plans.join(', ')}</div></td>
                    <td data-libelle="Cheminements" className="num">{g.nbCheminements}{g.nbNonMesures ? <div className="tres-petit discret">dont {g.nbNonMesures} sans échelle</div> : null}</td>
                    {(['totalM', 'reserveM', 'chutesM', 'aCommanderM'] as const).map((k) => (
                      <td key={k} data-libelle={{ totalM: 'Total posé', reserveM: 'Réserve', chutesM: 'Chutes', aCommanderM: 'À commander' }[k]} className="num">
                        {g[k] === null ? <span className="inconnu">Non calculé</span>
                          : <span className={g.fiable ? 'chiffre' : 'chiffre cch-non-fiable'}>{nombre(g[k], 2)} m{!g.fiable && <span className="visuellement-cache"> (non fiable)</span>}</span>}
                      </td>
                    ))}
                    <td data-libelle="Fiabilité">{g.fiable ? <Badge ton="succes">Échelle validée</Badge> : <Badge ton="ambre">Non fiable</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panneau>

      <ChargeFrigorifique x={x} dossierId={dossierId} revisionId={revision} />
      <Panneau titre="Nomenclature issue du schéma" serre>
        {!x.nomenclature.length ? <p className="petit discret" style={{ padding: 'var(--e-4) var(--e-5)' }}>Aucun équipement dans le schéma fonctionnel.</p> : (
          <div className="tableau-conteneur">
            <table className="tableau cch-tableau">
              <thead><tr><th>Type</th><th>Fabricant</th><th>Référence</th><th className="num">Quantité</th><th>Équipements</th></tr></thead>
              <tbody>
                {x.nomenclature.map((n) => (
                  <tr key={n.cle}>
                    <td data-libelle="Type">{n.typeLibelle}{n.existant && <div><Badge ton="neutre">Existant, non chiffré</Badge></div>}</td>
                    <td data-libelle="Fabricant">{n.fabricant ?? <span className="inconnu">Inconnu</span>}</td>
                    <td data-libelle="Référence">{n.reference ? <span className="mono">{n.reference}</span> : <span className="inconnu">À préciser</span>}</td>
                    <td data-libelle="Quantité" className="num">{n.quantite}</td>
                    <td data-libelle="Équipements" className="petit secondaire">{n.exemples.join(', ')}{n.quantite > n.exemples.length ? '…' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panneau>

      <Panneau titre="Ajouter au devis">
        <div className="pile">
          <p className="petit secondaire">
            Ajoute une ligne « câblage » par type de câble (en mètres, réserve et chutes comprises et détaillées dans le libellé) et une ligne « matériel »
            par équipement du schéma, ainsi que les tuyauteries par diamètre et isolant et les joints de branchement. Le prix vient du catalogue quand la référence y figure ; sinon il reste « À renseigner ». Un nouvel envoi ne duplique pas les lignes. Les écarts se traitent avec « Mettre à jour » dans le devis.
          </p>
          {brouillons.length > 0 && !raison && (
            <ChampChoix libelle="Devis brouillon" value={revision} onChange={(e) => setRevision(e.target.value)} options={brouillons} />
          )}
          {raison && <Alerte ton="ambre" titre="Envoi impossible pour l’instant">{raison}</Alerte>}
          <div className="groupe-btn">
            <Bouton variante="principal" icone={<FilePlus2 aria-hidden />} disabled={!!raison || !revision} chargement={envoi.isPending}
              onClick={() => envoi.mutate()}>Ajouter au devis</Bouton>
            {x.envoiDevisDisponible && !brouillons.length && !devis.isLoading && <LienBouton to={`/etudes/${dossierId}/devis`}>Ouvrir les devis du dossier</LienBouton>}
          </div>
        </div>
      </Panneau>
    </div>
  );
}
